import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import AccountBalanceIcon   from '@mui/icons-material/AccountBalance';
import AddCommentIcon       from '@mui/icons-material/AddComment';
import ArrowBackIcon        from '@mui/icons-material/ArrowBack';
import BadgeIcon            from '@mui/icons-material/Badge';
import ExpandMoreIcon       from '@mui/icons-material/ExpandMore';
import BusinessIcon         from '@mui/icons-material/Business';
import ChatIcon             from '@mui/icons-material/Chat';
import CheckIcon            from '@mui/icons-material/Check';
import DoneAllIcon          from '@mui/icons-material/DoneAll';
import ElderlyIcon          from '@mui/icons-material/Elderly';
import EmailIcon            from '@mui/icons-material/Email';
import ErrorOutlineIcon     from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon       from '@mui/icons-material/FolderOpen';
import GavelIcon            from '@mui/icons-material/Gavel';
import PercentIcon          from '@mui/icons-material/Percent';
import AccessibleIcon       from '@mui/icons-material/Accessible';
import LocalHospitalIcon    from '@mui/icons-material/LocalHospital';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import PaymentsIcon         from '@mui/icons-material/Payments';
import ShieldIcon           from '@mui/icons-material/Shield';
import PersonAddIcon        from '@mui/icons-material/PersonAdd';
import PersonIcon           from '@mui/icons-material/Person';
import SearchIcon           from '@mui/icons-material/Search';
import SendIcon             from '@mui/icons-material/Send';
import WhatsAppIcon         from '@mui/icons-material/WhatsApp';
import FamilyRestroomIcon   from '@mui/icons-material/FamilyRestroom';
import mensajeriaService    from '../services/mensajeriaService';
import { coincideBusqueda } from '../utils/textoComparable';

// ── Metadatos visuales ────────────────────────────────────────────────────────

const TIPO_TRAMITE_META = {
    RTI:        { label: 'RTI',        Icon: AccountBalanceIcon, color: '#2563eb' },
    ART:        { label: 'ART',        Icon: LocalHospitalIcon,  color: '#dc2626' },
    JUBILACION: { label: 'Jubilación', Icon: ElderlyIcon,        color: '#059669' },
    PUAM:       { label: 'PUAM',       Icon: BadgeIcon,          color: '#7c3aed' },
    PLAN_PAGO:  { label: 'Plan de Pago', Icon: PaymentsIcon,     color: '#0891b2' },
    REAJUSTE:   { label: 'Reajuste',     Icon: AccountBalanceIcon, color: '#ea580c' },
    // Apartados propios para los 3 módulos nuevos (Reconocimiento, PNC, Amparo PNC): cada uno
    // con su propio ícono/color, en vez de caer en el genérico (GavelIcon gris) del fallback.
    RECONOCIMIENTO: { label: 'Reconocimiento', Icon: FolderOpenIcon,         color: '#0369a1' },
    PNC:            { label: 'PNC',            Icon: MedicalInformationIcon, color: '#4338ca' },
    AMPARO_PNC:     { label: 'Amparo PNC',      Icon: ShieldIcon,             color: '#b91c1c' },
    AMPARO_LEY_16986: { label: 'Amparo Ley 16.986', Icon: ShieldIcon,         color: '#b91c1c' },
    GANANCIAS:      { label: 'Ganancias',      Icon: PercentIcon,            color: '#16a34a' },
    MINUSVALIA:     { label: 'Minusvalía',     Icon: AccessibleIcon,         color: '#4f46e5' },
    DECLARATORIA_HEREDEROS: { label: 'Declaratoria de Herederos', Icon: FamilyRestroomIcon, color: '#9f1239' },
    // Amparo por mora ante ANSES (V239): GavelIcon (el martillo judicial) porque es lo único de acá
    // que se presenta en el juzgado. Clave 'AMPARO' a secas; 'AMPARO_PNC' es el de arriba.
    AMPARO:         { label: 'Amparo por mora',  Icon: GavelIcon,              color: '#be123c' },
};

const TIPO_CONTACTO_META = {
    CLIENTE:            { label: 'Titular',         Icon: PersonIcon,        color: '#16a34a' },
    CONTACTO_ADICIONAL: { label: 'Contacto',        Icon: PersonAddIcon,     color: '#2563eb' },
    CLINICA:            { label: 'Clínica',          Icon: LocalHospitalIcon, color: '#dc2626' },
    ART:                { label: 'ART',             Icon: BusinessIcon,      color: '#d97706' },
    ORGANISMO:          { label: 'Organismo',        Icon: AccountBalanceIcon,color: '#7c3aed' },
    ABOGADO_EXTERNO:    { label: 'Abogado externo', Icon: GavelIcon,         color: '#374151' },
    LIBRE:              { label: 'Libre',            Icon: ChatIcon,          color: '#64748b' },
};

const CANAL_META = {
    WHATSAPP: { label: 'WhatsApp', Icon: WhatsAppIcon, color: '#25D366' },
    EMAIL:    { label: 'Email',    Icon: EmailIcon,    color: '#2563eb' },
    INTERNO:  { label: 'Interno',  Icon: ChatIcon,     color: '#64748b' },
};

const TIPOS_CONTACTO_OPCIONES = [
    { value: 'CLIENTE',            label: 'Titular' },
    { value: 'CONTACTO_ADICIONAL', label: 'Contacto adicional' },
    { value: 'CLINICA',            label: 'Clínica' },
    { value: 'ART',                label: 'ART' },
    { value: 'ORGANISMO',          label: 'Organismo' },
    { value: 'ABOGADO_EXTERNO',    label: 'Abogado externo' },
    { value: 'LIBRE',              label: 'Libre' },
];

const CANALES_OPCIONES = Object.entries(CANAL_META).map(([k, v]) => ({ value: k, label: v.label }));

// ── Helpers ───────────────────────────────────────────────────────────────────

const iniciales = (nombre) => {
    if (!nombre) return '?';
    const partes = nombre.replace(',', '').trim().split(/\s+/);
    if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
    return nombre[0].toUpperCase();
};

const formatTiempo = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const hoy = new Date();
    if (date.toDateString() === hoy.toDateString())
        return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatHora = (value) => {
    if (!value) return '--:--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--:--';
    return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const etiquetaConversacion = (conv) => {
    const prefijo = conv.tipoTramite ? `${conv.tipoTramite} #${conv.idTramite}` : '';
    const sufijo  = TIPO_CONTACTO_META[conv.tipoContacto]?.label || conv.nombreContacto || 'Contacto';
    if (prefijo && sufijo) return `${prefijo} — ${sufijo}`;
    return prefijo || sufijo;
};

// ── Sub-componentes ───────────────────────────────────────────────────────────

function BadgeNoLeidos({ count }) {
    if (!count || count <= 0) return null;
    return (
        <Box sx={{
            minWidth: 18, height: 18, px: 0.5, borderRadius: 999,
            bgcolor: '#16a34a', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
            {count > 99 ? '99+' : count}
        </Box>
    );
}

function renderEstadoMensaje(estado) {
    switch (estado) {
        case 'EN_COLA':   return <AccessTimeIcon sx={{ fontSize: 13, color: '#9ca3af' }} />;
        case 'ENVIADO':   return <CheckIcon sx={{ fontSize: 15, color: '#9ca3af' }} />;
        case 'ENTREGADO': return <DoneAllIcon sx={{ fontSize: 15, color: '#9ca3af' }} />;
        case 'LEIDO':     return <DoneAllIcon sx={{ fontSize: 15, color: '#3da5ff' }} />;
        case 'FALLIDO':   return <ErrorOutlineIcon sx={{ fontSize: 15, color: '#ef4444' }} />;
        default:          return <CheckIcon sx={{ fontSize: 15, color: '#9ca3af' }} />;
    }
}

// ── Formulario: nueva conversación global (con selector de trámite) ──────────

function FormNuevaConversacionGlobal({ tramites, onCrear, onCancelar, creando }) {
    const [idTramite,     setIdTramite]     = useState('');
    const [tipoContacto,  setTipoContacto]  = useState('CLIENTE');
    const [canal,         setCanal]         = useState('WHATSAPP');
    const [nombreContacto,setNombreContacto]= useState('');
    const [parentesco,    setParentesco]    = useState('');
    const [telefono,      setTelefono]      = useState('');
    const [email,         setEmail]         = useState('');

    const tramiteSeleccionado = tramites.find((t) => String(t.idTramite) === String(idTramite));

    const handleSubmit = () => {
        onCrear({
            idTramite: Number(idTramite),
            tipoContacto,
            canal,
            nombreContacto,
            parentesco: tipoContacto === 'CONTACTO_ADICIONAL' ? parentesco : null,
            telefonoContacto: telefono,
            emailContacto: email,
        });
    };

    const esValido = idTramite && nombreContacto.trim() &&
        (canal !== 'WHATSAPP' || telefono.trim()) &&
        (canal !== 'EMAIL'    || email.trim());

    return (
        <Paper elevation={8} sx={{ p: 2, m: 1.5, borderRadius: 2, border: '1px solid #cbd5e1', bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#0f172a' }}>
                Nueva conversación
            </Typography>
            <Stack spacing={1.5}>
                {/* Selector de trámite */}
                <TextField
                    select size="small" label="Trámite" value={idTramite}
                    onChange={(e) => setIdTramite(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                >
                    <option value="">— Seleccionar trámite —</option>
                    {tramites.map((t) => {
                        const meta = TIPO_TRAMITE_META[t.tipoTramite] || { label: t.tipoTramite };
                        return (
                            <option key={t.idTramite} value={t.idTramite}>
                                {meta.label} #{t.idTramite} — {t.nombreCliente}
                            </option>
                        );
                    })}
                </TextField>

                {tramiteSeleccionado && (
                    <>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                select size="small" label="Contacto" value={tipoContacto}
                                onChange={(e) => setTipoContacto(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ native: true }}
                                sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                            >
                                {TIPOS_CONTACTO_OPCIONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </TextField>
                            <TextField
                                select size="small" label="Canal" value={canal}
                                onChange={(e) => setCanal(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ native: true }}
                                sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                            >
                                {CANALES_OPCIONES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </TextField>
                        </Stack>
                        <TextField
                            size="small" label="Nombre" value={nombreContacto}
                            onChange={(e) => setNombreContacto(e.target.value)}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                        />
                        {tipoContacto === 'CONTACTO_ADICIONAL' && (
                            <TextField
                                size="small" label="Parentesco (ej: Hermano, Esposa)" value={parentesco}
                                onChange={(e) => setParentesco(e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                            />
                        )}
                        {canal === 'WHATSAPP' && (
                            <TextField
                                size="small" label="Teléfono" value={telefono}
                                onChange={(e) => setTelefono(e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                            />
                        )}
                        {canal === 'EMAIL' && (
                            <TextField
                                size="small" label="Email" value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                            />
                        )}
                    </>
                )}

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Typography
                        variant="body2"
                        onClick={onCancelar}
                        sx={{ cursor: 'pointer', color: '#64748b', alignSelf: 'center', '&:hover': { color: '#0f172a' } }}
                    >
                        Cancelar
                    </Typography>
                    <Box
                        component="button"
                        onClick={handleSubmit}
                        disabled={creando || !esValido}
                        sx={{
                            bgcolor: '#25D366', color: '#fff', border: 'none', borderRadius: 999,
                            px: 2, py: 0.5, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            '&:hover': { bgcolor: '#128c7e' },
                            '&:disabled': { bgcolor: '#cbd5e1', cursor: 'default' },
                        }}
                    >
                        {creando ? 'Creando...' : 'Crear'}
                    </Box>
                </Stack>
            </Stack>
        </Paper>
    );
}

// ── Vista 1: lista agrupada por cliente ──────────────────────────────────────

function ItemTramite({ tramite, onClick }) {
    const noLeidos = Number(tramite.noLeidosTotal || 0);
    const meta     = TIPO_TRAMITE_META[tramite.tipoTramite] || { Icon: GavelIcon, color: '#64748b', label: tramite.tipoTramite };
    const { Icon } = meta;

    return (
        <Box
            role="button" tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            sx={{
                pl: 3, pr: 2, py: 1.25,
                display: 'flex', alignItems: 'center', gap: 1.5,
                cursor: 'pointer',
                borderLeft: noLeidos > 0 ? '3px solid #16a34a' : '3px solid transparent',
                bgcolor: noLeidos > 0 ? '#f0fdf4' : '#fff',
                '&:hover': { bgcolor: '#f1f5f9' },
            }}
        >
            <Avatar sx={{ bgcolor: `${meta.color}18`, color: meta.color, width: 32, height: 32 }}>
                <Icon sx={{ fontSize: 15 }} />
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Chip
                            label={meta.label}
                            size="small"
                            sx={{
                                height: 17, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: `${meta.color}18`, color: meta.color,
                                border: `1px solid ${meta.color}40`,
                            }}
                        />
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 500 }}>
                            #{tramite.idTramite}
                        </Typography>
                        <BadgeNoLeidos count={noLeidos} />
                    </Stack>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', flexShrink: 0 }}>
                        {formatTiempo(tramite.fechaUltimoMensaje)}
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.2 }}>
                    <Chip
                        label={`${tramite.totalConversaciones} conv.`}
                        size="small"
                        sx={{ height: 14, fontSize: '0.58rem', bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }} noWrap>
                        {tramite.ultimoMensaje || 'Sin mensajes'}
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
}

function GrupoCliente({ grupo, onSeleccionar }) {
    const [abierto, setAbierto] = useState(true);

    const noLeidosTotal = grupo.tramites.reduce((sum, t) => sum + Number(t.noLeidosTotal || 0), 0);
    const ultimaFechaMs = grupo.tramites.reduce((max, t) => {
        const ts = t.fechaUltimoMensaje ? new Date(t.fechaUltimoMensaje).getTime() : 0;
        return ts > max ? ts : max;
    }, 0);

    return (
        <Box>
            {/* Cabecera del cliente — clicable para colapsar */}
            <Box
                role="button"
                tabIndex={0}
                onClick={() => setAbierto((v) => !v)}
                onKeyDown={(e) => e.key === 'Enter' && setAbierto((v) => !v)}
                sx={{
                    px: 2, py: 1,
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    borderTop: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: '#f1f5f9' },
                }}
            >
                <Avatar sx={{
                    width: 34, height: 34,
                    bgcolor: '#2563eb18', color: '#2563eb',
                    fontSize: '0.8rem', fontWeight: 700,
                    flexShrink: 0,
                }}>
                    {iniciales(grupo.nombre)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.875rem' }}
                            noWrap
                        >
                            {grupo.nombre}
                        </Typography>
                        <BadgeNoLeidos count={noLeidosTotal} />
                    </Stack>
                </Box>
                {ultimaFechaMs > 0 && !abierto && (
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', flexShrink: 0 }}>
                        {formatTiempo(new Date(ultimaFechaMs).toISOString())}
                    </Typography>
                )}
                <ExpandMoreIcon sx={{
                    color: '#94a3b8',
                    fontSize: 18,
                    flexShrink: 0,
                    transform: abierto ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                }} />
            </Box>

            {/* Trámites del cliente */}
            {abierto && grupo.tramites.map((tramite, i) => (
                <React.Fragment key={tramite.idTramite}>
                    <ItemTramite tramite={tramite} onClick={() => onSeleccionar(tramite)} />
                    {i < grupo.tramites.length - 1 && <Divider sx={{ ml: 6 }} />}
                </React.Fragment>
            ))}
        </Box>
    );
}

function VistaTramites({ tramites, cargando, busqueda, onBusqueda, onSeleccionar, onNuevaConversacion }) {
    const filtrados = useMemo(() => {
        if (!busqueda.trim()) return tramites;
        return tramites.filter((t) => coincideBusqueda(
            [t.nombreCliente, t.tipoTramite, String(t.idTramite), t.ultimoMensaje], busqueda));
    }, [tramites, busqueda]);

    // Agrupar por cliente (cuil como clave, nombreCliente como fallback)
    const grupos = useMemo(() => {
        const map = new Map();
        for (const t of filtrados) {
            const key = t.cuil || t.nombreCliente;
            if (!map.has(key)) {
                map.set(key, { cuil: t.cuil, nombre: t.nombreCliente, tramites: [] });
            }
            map.get(key).tramites.push(t);
        }
        // Ordenar grupos por fecha del mensaje más reciente de cualquiera de sus trámites
        return [...map.values()].sort((a, b) => {
            const maxFecha = (g) => g.tramites.reduce((max, t) => {
                const ts = t.fechaUltimoMensaje ? new Date(t.fechaUltimoMensaje).getTime() : 0;
                return ts > max ? ts : max;
            }, 0);
            return maxFecha(b) - maxFecha(a);
        });
    }, [filtrados]);

    return (
        <>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff', display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    value={busqueda}
                    onChange={(e) => onBusqueda(e.target.value)}
                    fullWidth size="small"
                    placeholder="Buscar trámite o cliente..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f1f5f9', height: 38, fontSize: '0.875rem' },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                />
                <Tooltip title="Nueva conversación">
                    <IconButton
                        size="small"
                        onClick={onNuevaConversacion}
                        sx={{ bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#128c7e' }, flexShrink: 0 }}
                    >
                        <AddCommentIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {cargando ? (
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : grupos.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <GavelIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {busqueda ? 'Sin resultados para tu búsqueda.' : 'No hay trámites con conversaciones activas.'}
                        </Typography>
                    </Box>
                ) : grupos.map((grupo) => (
                    <GrupoCliente
                        key={grupo.cuil || grupo.nombre}
                        grupo={grupo}
                        onSeleccionar={onSeleccionar}
                    />
                ))}
            </Box>
        </>
    );
}

// ── Vista 2: conversaciones de un trámite ─────────────────────────────────────

function ItemConversacion({ conv, onClick }) {
    const meta      = TIPO_CONTACTO_META[conv.tipoContacto] || TIPO_CONTACTO_META.LIBRE;
    const canalMeta = CANAL_META[conv.canal] || CANAL_META.INTERNO;
    const noLeidos  = Number(conv.noLeidos || 0);
    const { Icon: TipoIcon }  = meta;
    const { Icon: CanalIcon } = canalMeta;

    return (
        <Box
            role="button" tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            sx={{
                px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5,
                cursor: 'pointer',
                borderLeft: noLeidos > 0 ? '4px solid #16a34a' : '4px solid transparent',
                bgcolor: noLeidos > 0 ? '#f0fdf4' : '#fff',
                '&:hover': { bgcolor: '#f1f5f9' },
            }}
        >
            <Box sx={{ position: 'relative' }}>
                <Avatar sx={{ bgcolor: `${meta.color}18`, color: meta.color, width: 40, height: 40 }}>
                    <TipoIcon fontSize="small" />
                </Avatar>
                <Box sx={{
                    position: 'absolute', bottom: -2, right: -2,
                    width: 16, height: 16, borderRadius: '50%',
                    bgcolor: canalMeta.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid #fff',
                }}>
                    <CanalIcon sx={{ fontSize: 9, color: '#fff' }} />
                </Box>
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: noLeidos > 0 ? 700 : 500, color: '#0f172a', fontSize: '0.875rem' }}
                            noWrap
                        >
                            {conv.nombreContacto || meta.label}
                            {conv.tipoContacto === 'CONTACTO_ADICIONAL' && conv.parentesco && (
                                <Typography component="span" sx={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 400, ml: 0.5 }}>
                                    ({conv.parentesco})
                                </Typography>
                            )}
                        </Typography>
                        <Chip
                            label={meta.label}
                            size="small"
                            sx={{ height: 14, fontSize: '0.58rem', bgcolor: `${meta.color}18`, color: meta.color, fontWeight: 600 }}
                        />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <BadgeNoLeidos count={noLeidos} />
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                            {formatTiempo(conv.fechaUltimoMensaje)}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.2 }}>
                    <CanalIcon sx={{ fontSize: 11, color: canalMeta.color }} />
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.2 }} noWrap>
                        {conv.ultimoMensaje || 'Sin mensajes'}
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
}

function VistaConversaciones({ tramite, conversaciones, cargando, onVolver, onSeleccionar, onNuevaConversacion }) {
    const meta = TIPO_TRAMITE_META[tramite.tipoTramite] || { Icon: GavelIcon, color: '#64748b', label: tramite.tipoTramite };
    const { Icon: TipoIcon } = meta;

    return (
        <>
            <Box sx={{
                px: 1.5, py: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff',
                display: 'flex', alignItems: 'center', gap: 1,
            }}>
                <IconButton size="small" onClick={onVolver}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Avatar sx={{ bgcolor: `${meta.color}18`, color: meta.color, width: 32, height: 32 }}>
                    <TipoIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Chip
                            label={meta.label}
                            size="small"
                            sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${meta.color}18`, color: meta.color }}
                        />
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 500 }}>
                            #{tramite.idTramite}
                        </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }} noWrap>
                        {tramite.nombreCliente}
                    </Typography>
                </Box>
                <Tooltip title="Nueva conversación">
                    <IconButton size="small" onClick={onNuevaConversacion} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                        <AddCommentIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {cargando ? (
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : conversaciones.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <ChatIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            No hay conversaciones en este trámite.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: '#25D366', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                            onClick={onNuevaConversacion}
                        >
                            + Iniciar primera conversación
                        </Typography>
                    </Box>
                ) : conversaciones.map((conv, i) => (
                    <React.Fragment key={conv.id}>
                        <ItemConversacion conv={conv} onClick={() => onSeleccionar(conv)} />
                        {i < conversaciones.length - 1 && <Divider sx={{ ml: 7 }} />}
                    </React.Fragment>
                ))}
            </Box>
        </>
    );
}

// ── Vista 3: chat ─────────────────────────────────────────────────────────────

function VistaChat({ conversacion, mensajes, cargando, enviando, onVolver, onEnviar }) {
    const meta      = TIPO_CONTACTO_META[conversacion.tipoContacto] || TIPO_CONTACTO_META.LIBRE;
    const canalMeta = CANAL_META[conversacion.canal] || CANAL_META.INTERNO;
    const { Icon: TipoIcon }  = meta;
    const { Icon: CanalIcon } = canalMeta;
    const [texto, setTexto]    = useState('');
    const messagesEndRef       = useRef(null);
    const scrollBoxRef         = useRef(null);
    const prevCountRef         = useRef(0);

    useEffect(() => {
        const nuevo = mensajes.length;
        const prev  = prevCountRef.current;
        prevCountRef.current = nuevo;

        if (!scrollBoxRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollBoxRef.current;
        const cercaDelFondo = scrollHeight - scrollTop - clientHeight < 120;

        // Scroll automático solo si: carga inicial, usuario ya estaba al fondo, o llegó más mensajes
        if (prev === 0 || cercaDelFondo || nuevo > prev) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [mensajes]);

    const handleEnviar = () => {
        const msg = texto.trim();
        if (!msg || enviando) return;
        onEnviar(msg);
        setTexto('');
    };

    return (
        <>
            <Box sx={{
                px: 1.5, py: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff',
                display: 'flex', alignItems: 'center', gap: 1,
            }}>
                <IconButton size="small" onClick={onVolver}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar sx={{ bgcolor: `${meta.color}18`, color: meta.color, width: 34, height: 34 }}>
                        <TipoIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{
                        position: 'absolute', bottom: -2, right: -2,
                        width: 14, height: 14, borderRadius: '50%',
                        bgcolor: canalMeta.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1.5px solid #fff',
                    }}>
                        <CanalIcon sx={{ fontSize: 8, color: '#fff' }} />
                    </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }} noWrap>
                        {conversacion.nombreContacto || meta.label}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" sx={{ color: canalMeta.color, fontSize: '0.68rem', fontWeight: 600 }}>
                            {canalMeta.label}
                        </Typography>
                        {conversacion.telefonoContacto && (
                            <>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>·</Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                                    {conversacion.telefonoContacto}
                                </Typography>
                            </>
                        )}
                    </Stack>
                </Box>
            </Box>

            <Box ref={scrollBoxRef} sx={{
                flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5,
                display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: '#f4f7f9',
            }}>
                {cargando ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                        <CircularProgress size={22} />
                    </Box>
                ) : mensajes.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', bgcolor: '#fff', mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Todavía no hay mensajes en esta conversación.
                        </Typography>
                    </Paper>
                ) : mensajes.map((msg) => {
                    const esSalida = msg.direccion === 'SALIENTE';
                    return (
                        <Box key={msg.id} sx={{ display: 'flex', justifyContent: esSalida ? 'flex-end' : 'flex-start' }}>
                            <Paper elevation={0} sx={{
                                maxWidth: '86%', p: 1, borderRadius: 2,
                                bgcolor: esSalida ? '#dcf8c6' : '#fff',
                                border: '1px solid #dce3ea',
                            }}>
                                {!esSalida && msg.nombreEmisor && (
                                    <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                        {msg.nombreEmisor}
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1f2937', fontSize: '0.875rem' }}>
                                    {msg.contenido}
                                </Typography>
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.25 }}>
                                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                                        {formatHora(msg.fechaCreacion)}
                                    </Typography>
                                    {esSalida && renderEstadoMensaje(msg.estado)}
                                </Stack>
                            </Paper>
                        </Box>
                    );
                })}
                <div ref={messagesEndRef} />
            </Box>

            <Divider />

            <Box sx={{ p: 1, bgcolor: '#fff' }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEnviar(); } }}
                        placeholder="Escribir mensaje..."
                        fullWidth size="small" multiline maxRows={4}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f8fafc', fontSize: '0.875rem', py: 0.75, px: 1.5 } }}
                    />
                    <Tooltip title="Enviar (Enter)">
                        <span>
                            <IconButton
                                color="success"
                                disabled={enviando || !texto.trim()}
                                onClick={handleEnviar}
                                sx={{
                                    width: 36, height: 36, bgcolor: '#25D366', color: '#fff',
                                    '&:hover': { bgcolor: '#128c7e' },
                                    '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#f8fafc' },
                                }}
                            >
                                {enviando
                                    ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                    : <SendIcon fontSize="small" sx={{ width: 18, height: 18 }} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>
        </>
    );
}

// ── Formulario: nueva conversación vinculada al trámite ───────────────────────

function FormNuevaConversacion({ idTramite, onCrear, onCancelar, creando }) {
    const [tipoContacto,  setTipoContacto]  = useState('CLIENTE');
    const [canal,         setCanal]         = useState('WHATSAPP');
    const [nombreContacto,setNombreContacto]= useState('');
    const [parentesco,    setParentesco]    = useState('');
    const [telefono,      setTelefono]      = useState('');
    const [email,         setEmail]         = useState('');

    const handleSubmit = () => {
        onCrear({
            idTramite, tipoContacto, canal, nombreContacto,
            parentesco: tipoContacto === 'CONTACTO_ADICIONAL' ? parentesco : null,
            telefonoContacto: telefono, emailContacto: email,
        });
    };

    const esValido = nombreContacto.trim() &&
        (canal !== 'WHATSAPP' || telefono.trim()) &&
        (canal !== 'EMAIL'    || email.trim());

    return (
        <Paper elevation={8} sx={{ p: 2, m: 1.5, borderRadius: 2, border: '1px solid #cbd5e1', bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#0f172a' }}>
                Nueva conversación
            </Typography>
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                    <TextField
                        select size="small" label="Contacto" value={tipoContacto}
                        onChange={(e) => setTipoContacto(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true }}
                        sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                    >
                        {TIPOS_CONTACTO_OPCIONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </TextField>
                    <TextField
                        select size="small" label="Canal" value={canal}
                        onChange={(e) => setCanal(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true }}
                        sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                    >
                        {CANALES_OPCIONES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </TextField>
                </Stack>
                <TextField
                    size="small" label="Nombre" value={nombreContacto}
                    onChange={(e) => setNombreContacto(e.target.value)}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                />
                {tipoContacto === 'CONTACTO_ADICIONAL' && (
                    <TextField
                        size="small" label="Parentesco (ej: Hermano, Esposa)" value={parentesco}
                        onChange={(e) => setParentesco(e.target.value)}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                    />
                )}
                {canal === 'WHATSAPP' && (
                    <TextField
                        size="small" label="Teléfono" value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                    />
                )}
                {canal === 'EMAIL' && (
                    <TextField
                        size="small" label="Email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem', height: 36 } }}
                    />
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Typography
                        variant="body2"
                        onClick={onCancelar}
                        sx={{ cursor: 'pointer', color: '#64748b', alignSelf: 'center', '&:hover': { color: '#0f172a' } }}
                    >
                        Cancelar
                    </Typography>
                    <Box
                        component="button"
                        onClick={handleSubmit}
                        disabled={creando || !esValido}
                        sx={{
                            bgcolor: '#25D366', color: '#fff', border: 'none', borderRadius: 999,
                            px: 2, py: 0.5, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            '&:hover': { bgcolor: '#128c7e' },
                            '&:disabled': { bgcolor: '#cbd5e1', cursor: 'default' },
                        }}
                    >
                        {creando ? 'Creando...' : 'Crear'}
                    </Box>
                </Stack>
            </Stack>
        </Paper>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────

function WhatsAppPanelDrawer({ onUnreadChatsChange }) {
    const [vista, setVista] = useState('tramites');
    const [tramiteSeleccionado,      setTramiteSeleccionado]      = useState(null);
    const [conversacionSeleccionada, setConversacionSeleccionada] = useState(null);

    const [tramites,         setTramites]         = useState([]);
    const [cargandoTramites, setCargandoTramites] = useState(true);

    // Las conversaciones del trámite seleccionado vienen embebidas en el DTO
    const [conversaciones,         setConversaciones]         = useState([]);
    const [cargandoConversaciones, setCargandoConversaciones] = useState(false);

    const [mensajes,        setMensajes]        = useState([]);
    const [cargandoMensajes,setCargandoMensajes]= useState(false);
    const [enviando,        setEnviando]        = useState(false);

    const [busqueda,            setBusqueda]            = useState('');
    const [mostrarNuevaConv,    setMostrarNuevaConv]    = useState(false);
    const [mostrarNuevaConvGlobal, setMostrarNuevaConvGlobal] = useState(false);
    const [creandoConv,         setCreandoConv]         = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, mensaje: '', severity: 'error' });

    // ── Carga de trámites ─────────────────────────────────────────────────────

    const cargarTramites = useCallback(async (silencioso = false) => {
        try {
            if (!silencioso) setCargandoTramites(true);
            const data = await mensajeriaService.listarTramites();
            const lista = Array.isArray(data) ? data : [];
            setTramites(lista);

            // Si hay un trámite seleccionado, actualizar sus conversaciones desde los datos frescos
            setTramiteSeleccionado((prev) => {
                if (!prev) return prev;
                const actualizado = lista.find((t) => t.idTramite === prev.idTramite);
                if (actualizado) setConversaciones(actualizado.conversaciones || []);
                return actualizado || prev;
            });
        } catch {
            if (!silencioso) setTramites([]);
        } finally {
            if (!silencioso) setCargandoTramites(false);
        }
    }, []);

    useEffect(() => { cargarTramites(); }, [cargarTramites]);

    useEffect(() => {
        const id = setInterval(() => cargarTramites(true), 30000);
        return () => clearInterval(id);
    }, [cargarTramites]);

    // Notificar no leídos totales al componente padre
    useEffect(() => {
        const total = tramites.reduce((acc, t) => acc + Number(t.noLeidosTotal || 0), 0);
        onUnreadChatsChange(total);
        window.dispatchEvent(new CustomEvent('whatsappUnreadCountChanged', { detail: { total } }));
    }, [tramites, onUnreadChatsChange]);

    // ── Selección de trámite ──────────────────────────────────────────────────

    const seleccionarTramite = useCallback((tramite) => {
        setTramiteSeleccionado(tramite);
        setConversaciones(tramite.conversaciones || []);
        setMostrarNuevaConv(false);
        setVista('conversaciones');
    }, []);

    // ── Carga de mensajes ─────────────────────────────────────────────────────

    const cargarMensajes = useCallback(async (idConversacion, silencioso = false) => {
        if (!silencioso) setCargandoMensajes(true);
        try {
            const data = await mensajeriaService.listarMensajes(idConversacion);
            const lista = Array.isArray(data) ? data : (data?.content ?? []);
            setMensajes(lista);
            await mensajeriaService.marcarLeida(idConversacion);
            setConversaciones((prev) => prev.map((c) => c.id === idConversacion ? { ...c, noLeidos: 0 } : c));
            cargarTramites(true);
        } catch {
            if (!silencioso) setMensajes([]);
        } finally {
            if (!silencioso) setCargandoMensajes(false);
        }
    }, [cargarTramites]);

    const seleccionarConversacion = useCallback((conv) => {
        setConversacionSeleccionada(conv);
        setVista('chat');
        cargarMensajes(conv.id);
    }, [cargarMensajes]);

    // Refresco silencioso de mensajes mientras hay una conversación abierta
    useEffect(() => {
        if (!conversacionSeleccionada) return;
        const id = setInterval(() => cargarMensajes(conversacionSeleccionada.id, true), 20000);
        return () => clearInterval(id);
    }, [conversacionSeleccionada, cargarMensajes]);

    // ── Enviar mensaje ────────────────────────────────────────────────────────

    const handleEnviarMensaje = async (texto) => {
        if (!conversacionSeleccionada || !texto.trim() || enviando) return;
        setEnviando(true);
        try {
            await mensajeriaService.enviarMensaje(conversacionSeleccionada.id, {
                contenido: texto.trim(),
                canal: conversacionSeleccionada.canal,
            });
            await cargarMensajes(conversacionSeleccionada.id);
        } catch {
            setSnackbar({ open: true, mensaje: 'No se pudo enviar el mensaje.', severity: 'error' });
        } finally {
            setEnviando(false);
        }
    };

    // ── Nueva conversación ────────────────────────────────────────────────────

    const handleCrearConversacion = async (payload) => {
        setCreandoConv(true);
        try {
            const nueva = await mensajeriaService.crearConversacion(payload);
            setMostrarNuevaConv(false);
            setMostrarNuevaConvGlobal(false);
            await cargarTramites(false);
            if (nueva?.id) seleccionarConversacion(nueva);
        } catch {
            setSnackbar({ open: true, mensaje: 'No se pudo crear la conversación.', severity: 'error' });
        } finally {
            setCreandoConv(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f4f7f9' }}>

                {vista === 'tramites' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                        <VistaTramites
                            tramites={tramites}
                            cargando={cargandoTramites}
                            busqueda={busqueda}
                            onBusqueda={setBusqueda}
                            onSeleccionar={seleccionarTramite}
                            onNuevaConversacion={() => { setBusqueda(''); setMostrarNuevaConvGlobal((v) => !v); }}
                        />
                        {mostrarNuevaConvGlobal && (
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 }}>
                                <FormNuevaConversacionGlobal
                                    tramites={tramites}
                                    onCrear={handleCrearConversacion}
                                    onCancelar={() => setMostrarNuevaConvGlobal(false)}
                                    creando={creandoConv}
                                />
                            </Box>
                        )}
                    </Box>
                )}

                {vista === 'conversaciones' && tramiteSeleccionado && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                        <VistaConversaciones
                            tramite={tramiteSeleccionado}
                            conversaciones={conversaciones}
                            cargando={cargandoConversaciones}
                            onVolver={() => { setVista('tramites'); setMostrarNuevaConv(false); }}
                            onSeleccionar={seleccionarConversacion}
                            onNuevaConversacion={() => setMostrarNuevaConv((v) => !v)}
                        />
                        {mostrarNuevaConv && (
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 }}>
                                <FormNuevaConversacion
                                    idTramite={tramiteSeleccionado.idTramite}
                                    onCrear={handleCrearConversacion}
                                    onCancelar={() => setMostrarNuevaConv(false)}
                                    creando={creandoConv}
                                />
                            </Box>
                        )}
                    </Box>
                )}

                {vista === 'chat' && conversacionSeleccionada && (
                    <VistaChat
                        conversacion={conversacionSeleccionada}
                        mensajes={mensajes}
                        cargando={cargandoMensajes}
                        enviando={enviando}
                        onVolver={() => { setVista('conversaciones'); setMensajes([]); }}
                        onEnviar={handleEnviarMensaje}
                    />
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.mensaje}
                </Alert>
            </Snackbar>
        </>
    );
}

WhatsAppPanelDrawer.propTypes = {
    onUnreadChatsChange: PropTypes.func,
};

WhatsAppPanelDrawer.defaultProps = {
    onUnreadChatsChange: () => {},
};

export default WhatsAppPanelDrawer;
